import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Autocomplete, {
	AutocompleteRenderInputParams,
} from '@material-ui/lab/Autocomplete/Autocomplete';
import parse from 'autosuggest-highlight/parse';
import React, { ChangeEvent, useState } from 'react';
import { Address } from '../../../lib/services/googlemaps/Address';
import { GmScriptLoader } from '../../../lib/services/googlemaps/GmScriptLoader';
import {
	extractAddressParts,
	fetchPlacePredictions,
	geocodeByAddress,
	googleMapsApiLoaded,
} from '../../../lib/services/googlemaps/gmUtils';
import { GenericInput, GenericInputProps } from '../GenericInput/GenericInput';

const useStyles = makeStyles((theme) => ({
	icon: {
		color: theme.palette.text.secondary,
		marginRight: theme.spacing(2),
	},
}));

interface AddressInputProps {
	nestedInputProps: GenericInputProps;
	onAddressCompleted(address?: Address): boolean;
}

/**
 * A MaterialUI Autocomplete Input that fetches options from the Google Maps API
 * @param props the component props of type AddressInputProps
 */
export const AddressInput: React.FC<AddressInputProps> = (props) => {
	const classes = useStyles();
	const [value, setValue] = React.useState(props.nestedInputProps.inputValue);
	const [open, setOpen] = React.useState(false);
	const [options, setOptions] = useState<
		google.maps.places.AutocompletePrediction[]
	>([]);
	const [lastAddressStreet, setLastAddressStreet] = React.useState('');

	// handle script load error
	const handleGoogleMapsError = () => {
		console.error('handleGoogleMapsError');
	};

	// perform lookup of structured address data from address string, update form fields
	const fetchGeocodedAddress = (addr: string): void => {
		// prevent search request with exact street result from last request
		if (addr === lastAddressStreet) {
			return;
		}

		geocodeByAddress(addr)
			.then((results) => extractAddressParts(results))
			.then((address) => {
				setLastAddressStreet(address.street);
				setValue(address.street);
				setOpen(false);
				props.onAddressCompleted(address);
			})
			.catch((error) => console.error('Error', error));
	};

	// act on input value changes and update autocomplete options
	React.useEffect(() => {
		let active = true;

		// fetchPlacePredictions() throws an error if API isn't loaded
		if (!googleMapsApiLoaded()) {
			return undefined;
		}

		if (value === '') {
			setOptions([]);

			return undefined;
		}

		fetchPlacePredictions(
			{
				input: value,
				types: ['address'],
				componentRestrictions: { country: 'de' },
			},
			(result) => {
				if (active) {
					setOptions(result || []);
				}
			}
		);

		return () => {
			active = false;
		};
	}, [value]);

	// Event listener for input event on the input field
	const onInputChange = (
		event: React.ChangeEvent<{}>,
		val: string,
		reason: 'input' | 'reset' | 'clear'
	) => {
		if (event && reason === 'input') {
			// When the user types something into the input field, we propagate this value to update the states of the
			// wrapping components
			setValue(val);
		} else if (event && reason === 'reset' && props.onAddressCompleted()) {
			// When the user selects an option from the options list, the full address should be retrieved from the
			// Geocoder API. This user action comes with an event object and the reason 'reset'. Critically, setting
			// overrideValue programmatically will trigger onInputChange() again - but can be distinguished by having
			// no event object. We don't want to trigger another address lookup in that case.
			fetchGeocodedAddress(val);
		} else if (reason === 'clear' && props.onAddressCompleted()) {
			// When the user clears the street name (by using the X icon), the other address parts should be removed
			// from the adjacent input fields
			props.onAddressCompleted();
		}
	};

	// event listener on Enter key press events to perform address lookup
	const onKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			fetchGeocodedAddress(value);
		}
	};

	// event listener on changed value to set component state (Autocomplete is in controlled mode)
	// tslint:disable-next-line:no-any
	const onChange = (event: ChangeEvent<{}>, newValue: any) => {
		// check if newValue is an AutocompletePrediction
		if (newValue !== null && 'description' in newValue) {
			setValue(
				(newValue as google.maps.places.AutocompletePrediction).description
			);
		} else {
			setValue(newValue ?? '');
		}
	};

	// controlled opening of the options flyout
	const onOpen = () => {
		setOpen(true);
	};

	// controlled closing of the options flyout
	const onClose = () => {
		setOpen(false);
	};

	// render method for input field
	const renderInput = (params: AutocompleteRenderInputParams) => (
		<GenericInput
			{...props.nestedInputProps}
			setInputValue={(val) => {
				props.nestedInputProps.setInputValue(val);
				setValue(val);
			}}
			additionalInputParams={{
				...params,
				...props.nestedInputProps.additionalInputParams,
			}}
		/>
	);

	// render method for each autocomplete suggestion
	const renderOption = (option: any) => {
		const matches = option.structured_formatting.main_text_matched_substrings;
		const parts = parse(
			option.structured_formatting.main_text,
			matches.map((match: any) => [match.offset, match.offset + match.length])
		);

		return (
			<Grid container alignItems="center">
				<Grid item>
					<LocationOnIcon className={classes.icon} />
				</Grid>
				<Grid item xs>
					{parts.map((part, index) => (
						<span
							key={index}
							style={{ fontWeight: part.highlight ? 700 : 400 }}
						>
							{part.text}
						</span>
					))}
					<Typography variant="body2" color="textSecondary">
						{option.structured_formatting.secondary_text}
					</Typography>
				</Grid>
			</Grid>
		);
	};

	return (
		<>
			<GmScriptLoader onError={handleGoogleMapsError} />
			<Autocomplete
				id={props.nestedInputProps.inputId}
				style={{ width: '100%' }}
				getOptionLabel={(option) =>
					typeof option === 'string' ? option : option.description
				}
				filterOptions={(x) => x}
				options={options}
				autoComplete
				includeInputInList
				freeSolo
				renderInput={renderInput}
				renderOption={renderOption}
				onInputChange={onInputChange}
				onKeyPress={onKeyPress}
				onChange={onChange}
				onOpen={onOpen}
				onClose={onClose}
				value={value}
				open={open}
			/>
		</>
	);
};
